<template>
    <div>
        <template v-if="isBusy">
            <Preloader />
        </template>

        <template v-else>
            <GarantieHeader title="Garantie Moto" />
            <GarantieStepper :currentStep="currentStep" :steps="steps" @update:currentStep="handleStepChange" />

            <form name="devis_panne_mecanique_moto" @submit.prevent="onSubmit">
                <div class="row">
                    <div class="col-12 form-card-container">
                        <div class="form-card">
                            <!-- STEP 1 -->
                            <template v-if="currentStep === 1">
                                <div class="row m-0 align-items-center">
                                    <div
                                        :class="(isVehicleSearching || searchVehicleFilled) ? 'col-lg-7 pr-lg-4' : 'col-lg-12'">
                                        <div class="form-card-header">
                                            <h2 class="form-card-title">Informations sur le véhicule à assurer</h2>
                                            <span class="required-notice">(* champs obligatoires)</span>
                                        </div>

                                        <!-- Step 1 - Field 1: Registration -->
                                        <div class="form-row-grid mb-0">
                                            <div class="form-group">
                                                <label class="form-label">Immatriculation <span
                                                        class="required">*</span></label>
                                                <div class="form-input-container">
                                                    <div class="registration-search-wrapper">
                                                        <input type="text" v-model="garantieMoto.immatriculation"
                                                            placeholder="Ex: AF-785-HT" maxlength="10"
                                                            @input="handleImmatriculationInput($event)"
                                                            @keydown.enter.prevent="searchByRegNumberImmediately"
                                                            class="redesign-input registration-input-field"
                                                            :class="{ 'input-error': hasError('immatriculation') }" />
                                                    </div>
                                                    <div v-if="hasError('immatriculation')" class="error-message">
                                                        {{ getError('immatriculation') }}
                                                    </div>
                                                </div>
                                                <div v-if="showManualEntryLink || !garantieMoto.immatriculation"
                                                    class="registration-help">
                                                    <a @click="showManualEntryPopup = true" class="manual-entry-link">
                                                        Je ne connais pas mon immatriculation
                                                    </a>
                                                </div>
                                                <div v-else-if="isManualEntry" class="registration-help">
                                                    <a @click="showManualEntryPopup = true" class="manual-entry-link">
                                                        Modifier les informations du véhicule
                                                    </a>
                                                </div>
                                            </div>
                                            <div class="form-group" v-if="!(isVehicleSearching || searchVehicleFilled)">
                                            </div>
                                        </div>

                                        <div class="form-row-grid">
                                            <div class="form-group">
                                                <label class="form-label">Kilométrage <span
                                                        class="required">*</span></label>
                                                <div class="form-input-container">
                                                    <input type="text" v-model="garantieMoto.km_jour_vente"
                                                        placeholder="Ex: 50000" class="redesign-input"
                                                        @input="handleKmInput($event)"
                                                        :class="{ 'input-error': hasError('km_jour_vente') }" />
                                                    <div v-if="hasError('km_jour_vente')" class="error-message">{{
                                                        getError('km_jour_vente') }}
                                                    </div>
                                                </div>
                                            </div>

                                            <div class="form-group">
                                                <label class="form-label">Date d'effet <span
                                                        class="required">*</span></label>
                                                <div
                                                    class="form-input-container date-input-container currency-datepicker">
                                                    <CustomDatepicker :date.sync="effectiveDateInput"
                                                        :disableDate="true" :has-error="hasError('effective_date')"
                                                        class="redesign-datepicker" />
                                                    <div v-if="hasError('effective_date')" class="error-message">{{
                                                        getError('effective_date') }}</div>
                                                </div>
                                            </div>
                                            <div class="form-group">
                                                <label class="form-label">Appellation commerciale</label>
                                                <div class="form-input-container">
                                                    <input type="text" v-model="garantieMoto.app_commerciale"
                                                        placeholder="Ex: Z900" class="redesign-input"
                                                        :class="{ 'input-error': hasError('app_commerciale') }" />
                                                    <div v-if="hasError('app_commerciale')" class="error-message">{{
                                                        getError('app_commerciale') }}</div>
                                                </div>
                                            </div>
                                        </div>

                                        <!-- Required Hidden Fields -->
                                        <div style="display: none;">
                                            <input type="hidden" v-model="garantieMoto.marque_auto" />
                                            <input type="hidden" v-model="garantieMoto.modele_porteur" />
                                            <input type="hidden" v-model="garantieMoto.cylindree" />
                                            <input type="hidden" v-model="garantieMoto.num_serie" />
                                            <CustomDatepicker :date.sync="circulationDateInput" />
                                        </div>
                                    </div>
                                    <!-- Vehicle Summary Card -->
                                    <div class="col-lg-5" v-if="isVehicleSearching || searchVehicleFilled">
                                        <div v-if="isVehicleSearching" class="vehicle-search-loader">
                                            <Preloader wrapperHeight="250px" />
                                        </div>
                                        <VehicleSummaryCard v-else :details="vehicleSummary" />
                                    </div>
                                </div>


                                <div class="form-card-footer-nav justify-content-center align-items-center">
                                    <div class="eligibility-action-wrapper">
                                        <button type="button" class="btn-primary-outline"
                                            :class="{ 'disabled': (isVehicleTooOld && !isManualEntry) || !searchVehicleFilled }"
                                            :disabled="(isVehicleTooOld && !isManualEntry) || !searchVehicleFilled"
                                            @click="goToNextStep">
                                            Suivant
                                        </button>

                                        <div v-if="isVehicleTooOld" class="eligibility-info-trigger"
                                            title="Véhicule non éligible : voir instructions">
                                            <font-awesome-icon icon="info-circle" />
                                            <div class="eligibility-popover">
                                                <div class="popover-title">
                                                    <font-awesome-icon icon="exclamation-triangle" />
                                                    Informations sur l'éligibilité
                                                </div>
                                                <p class="popover-desc">Les critères du véhicule ne permettent pas de
                                                    souscrire à la garantie choisie.</p>
                                                <p class="popover-desc">
                                                    Pour effectuer une demande de dérogation, veuillez adresser le
                                                    contrôle technique à :
                                                    <a
                                                        href="mailto:servicetechnique@dynassurances.fr">servicetechnique@dynassurances.fr</a>
                                                </p>
                                                <div class="popover-desc mb-1">Informations requises :</div>
                                                <ul class="popover-list">
                                                    <li>Immatriculation du véhicule</li>
                                                    <li>Votre code garage</li>
                                                </ul>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </template>

                            <!-- STEP 2 -->
                            <template v-else-if="currentStep === 2">
                                <div class="form-card-header">
                                    <h2 class="form-card-title">Information sur la garantie</h2>
                                    <span class="required-notice">(* champs obligatoires)</span>
                                </div>

                                <div class="form-row-grid">
                                    <label class="form-label col-3">Choisissez une durée <span
                                            class="required">*</span></label>
                                    <div class="redesign-option-group highlighted-options col-8 pr-2">
                                        <button v-for="item in durationOptions" :key="item.value" type="button"
                                            class="option-btn"
                                            :class="{ 'active': garantieMoto.duree == item.value, 'option-btn-error': hasError('duree') }"
                                            @click="garantieMoto.duree = item.value">
                                            {{ item.name }}
                                        </button>
                                    </div>
                                    <div v-if="hasError('duree')" class="error-message">{{ getError('duree') }}
                                    </div>
                                </div>

                                <!-- Plans Section -->
                                <InsurancePlansCarousel :apiUrl="'/moto/calculate'" :plans="availablePlans"
                                    :selectedPlanId="garantieMoto.garantie" :duration="garantieMoto.duree"
                                    :vehicle="garantieMoto"
                                    @select-plan="(id, price) => { garantieMoto.garantie = id; selectedPlanPrice = price; }"
                                    @go-to-step="currentStep = $event" />

                                <div class="form-card-footer-nav">
                                    <button type="button" class="btn-secondary-outline" @click="currentStep--">
                                        Retour
                                    </button>
                                    <button type="button" class="btn-primary-outline" @click="goToNextStep">
                                        Suivant
                                    </button>
                                </div>
                            </template>

                            <!-- STEP 3 -->
                            <template v-else-if="currentStep === 3">
                                <div class="form-card-header">
                                    <h2 class="form-card-title">Informations sur le propriétaire du véhicule</h2>
                                    <span class="required-notice">(* champs obligatoires)</span>
                                </div>

                                <div class="row step-3-row">
                                    <div class="col-12 col-lg-8 col-xl-8 pr-xl-4">
                                        <div class="form-row-grid">
                                            <div class="form-group flex-width-sm">
                                                <label class="form-label">Civilité</label>
                                                <div class="form-input-container">
                                                    <select v-model="garantieMoto.civilite" class="redesign-select">
                                                        <option value="0">M</option>
                                                        <option value="1">Mlle</option>
                                                        <option value="2">Mme</option>
                                                        <option value="3">Société</option>
                                                        <option value="4">Mr et Mme</option>
                                                    </select>
                                                </div>
                                            </div>

                                            <div class="form-group">
                                                <label class="form-label">
                                                    {{ garantieMoto.civilite == 3 ? 'Prénom du signataire' : 'Prénom' }}
                                                    <span class="required">*</span>
                                                </label>
                                                <div class="form-input-container">
                                                    <input type="text" v-model="garantieMoto.prenom"
                                                        class="redesign-input"
                                                        :class="{ 'input-error': hasError('prenom') }" />
                                                    <div v-if="hasError('prenom')" class="error-message">{{
                                                        getError('prenom')
                                                        }}
                                                    </div>
                                                </div>
                                            </div>

                                            <div class="form-group">
                                                <label class="form-label">
                                                    {{ garantieMoto.civilite == 3 ? 'Nom du signataire' : 'Nom' }}
                                                    <span class="required">*</span>
                                                </label>
                                                <div class="form-input-container">
                                                    <input type="text" v-model="garantieMoto.nom" class="redesign-input"
                                                        :class="{ 'input-error': hasError('nom') }" />
                                                    <div v-if="hasError('nom')" class="error-message">{{ getError('nom')
                                                        }}
                                                    </div>
                                                </div>
                                            </div>
                                        </div>

                                        <template v-if="garantieMoto.civilite == 3">
                                            <div class="form-row">
                                                <div class="form-group full-width">
                                                    <label class="form-label">Société <span
                                                            class="required">*</span></label>
                                                    <div class="form-input-container">
                                                        <input type="text" v-model="garantieMoto.name_of_signatory"
                                                            class="redesign-input"
                                                            :class="{ 'input-error': hasError('name_of_signatory') }" />
                                                        <div v-if="hasError('name_of_signatory')" class="error-message">
                                                            {{
                                                                getError('name_of_signatory') }}</div>
                                                    </div>
                                                </div>
                                            </div>
                                        </template>

                                        <div class="form-row">
                                            <div class="form-group full-width">
                                                <label class="form-label">Adresse <span
                                                        class="required">*</span></label>
                                                <div class="form-input-container">
                                                    <input type="text" v-model="garantieMoto.adresse"
                                                        class="redesign-input"
                                                        :class="{ 'input-error': hasError('adresse') }" />
                                                    <div v-if="hasError('adresse')" class="error-message">{{
                                                        getError('adresse')
                                                        }}
                                                    </div>
                                                </div>
                                            </div>
                                        </div>

                                        <div class="form-row-grid">
                                            <div class="form-group">
                                                <label class="form-label">Code postal <span
                                                        class="required">*</span></label>
                                                <div class="form-input-container">
                                                    <input type="text" v-model="garantieMoto.cp" maxlength="5"
                                                        class="redesign-input"
                                                        :class="{ 'input-error': hasError('cp') }" />
                                                    <div v-if="hasError('cp')" class="error-message">{{ getError('cp')
                                                        }}</div>
                                                </div>
                                            </div>

                                            <div class="form-group">
                                                <label class="form-label">Ville <span class="required">*</span></label>
                                                <div class="form-input-container">
                                                    <input type="text" v-model="garantieMoto.ville"
                                                        class="redesign-input"
                                                        :class="{ 'input-error': hasError('ville') }" />
                                                    <div v-if="hasError('ville')" class="error-message">{{
                                                        getError('ville') }}
                                                    </div>
                                                </div>
                                            </div>
                                        </div>

                                        <div class="form-row-grid">
                                            <div class="form-group">
                                                <label class="form-label">Téléphone</label>
                                                <div class="form-input-container">
                                                    <input type="text" v-model="garantieMoto.tel" maxlength="10"
                                                        class="redesign-input"
                                                        :class="{ 'input-error': hasError('tel') }" />
                                                    <div v-if="hasError('tel')" class="error-message">{{ getError('tel')
                                                        }}
                                                    </div>
                                                </div>
                                            </div>
                                            <div class="form-group">
                                                <label class="form-label">Email <span class="required">*</span></label>
                                                <div class="form-input-container">
                                                    <input type="email" v-model="garantieMoto.email"
                                                        class="redesign-input"
                                                        :class="{ 'input-error': hasError('email') }" />
                                                    <div v-if="hasError('email')" class="error-message">{{
                                                        getError('email')
                                                    }}
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                        <div class="form-group">
                                            <label class="form-label">Fax</label>
                                            <div class="form-input-container">
                                                <input type="text" v-model="garantieMoto.fax" maxlength="10"
                                                    class="redesign-input"
                                                    :class="{ 'input-error': hasError('fax') }" />
                                                <div v-if="hasError('fax')" class="error-message">{{ getError('fax')
                                                    }}
                                                </div>
                                            </div>
                                        </div>

                                        <div v-if="mechanicClientPayment">
                                            <!-- <div class="form-group full-width">
                                                <label class="form-label">Les coordonnées de votre compte <span
                                                        class="text-muted">(Exemple :
                                                        FR7630006000011234567890189)</span></label>
                                                <div class="form-input-container">
                                                    <input type="text" v-model="garantieMoto.iban"
                                                        class="redesign-input"
                                                        :class="{ 'input-error': hasError('iban') }" />
                                                    <div v-if="hasError('iban')" class="error-message">{{
                                                        getError('iban') }}
                                                    </div>
                                                </div>
                                            </div> -->
                                            <div class="form-group full-width">
                                                <label class="form-label">Date de naissance</label>
                                                <div
                                                    class="form-input-container date-input-container currency-datepicker">
                                                    <CustomDatepicker :date.sync="birthDateInput" :birthdate="true"
                                                        :has-error="hasError('date_of_birth')"
                                                        class="redesign-datepicker" />
                                                    <div v-if="hasError('date_of_birth')" class="error-message">
                                                        {{ getError('date_of_birth') }}
                                                    </div>
                                                </div>
                                            </div>
                                        </div>


                                    </div>

                                    <!-- Summary Column -->
                                    <div class="col-12 col-lg-4 col-xl-4">
                                        <SelectedPlanSummary :plan="{
                                            name: selectedPlanObject ? selectedPlanObject.name : '',
                                            price: selectedPlanPrice || (selectedPlanObject ? selectedPlanObject.basePrice : 0),
                                            key: selectedPlanObject ? selectedPlanObject.key : ''
                                        }" :duration="selectedDurationName" :features="selectedPlanDetailedFeatures"
                                            :vehicle="vehicleSummary"
                                            :termsUrl="selectedPlanObject ? getUrl(selectedPlanObject) : ''" />
                                    </div>

                                    <div class="form-card-footer-nav col-12">
                                        <button type="button" class="btn-secondary-outline" @click="currentStep--">
                                            Retour
                                        </button>
                                        <button type="submit" class="btn-primary-outline">
                                            VOIR LE DEVIS
                                        </button>
                                    </div>
                                </div>
                            </template>

                            <!-- STEP 4 -->
                            <template v-if="currentStep === 4 || currentStep === 5">
                                <GarantieMotoView @change-step="goToNextStep" :current-step="currentStep"
                                    :custom-class="'p-0 m-0 bg-transparent'" :insurance-data="savedQuote" />
                                <div class="form-card-footer-nav col-12">
                                    <button type="button" class="btn-secondary-outline" @click="currentStep--">
                                        Retour
                                    </button>

                                </div>
                            </template>
                        </div>
                    </div>
                </div>

            </form>

            <ManualVehicleEntryModal :is-visible="showManualEntryPopup" :initial-data="garantieMoto" category="moto"
                @close="showManualEntryPopup = false" @submit="handleManualEntrySubmit" />
        </template>
    </div>
</template>

<script>
import garantieFieldsLists from "@/mixins/garantieFieldsLists";
import Preloader from "@/components/Preloader";
import { DEBOUNCE } from "@/mixins/helper";
import InsurancePlansCarousel from "@/components/Garantie/shared/InsurancePlansCarousel";
import SelectedPlanSummary from "@/components/Garantie/shared/SelectedPlanSummary";
import GarantieHeader from "@/components/Garantie/shared/GarantieHeader";
import GarantieStepper from "@/components/Garantie/shared/GarantieStepper";
import ManualVehicleEntryModal from "@/components/Garantie/shared/ManualVehicleEntryModal";
import manualVehicleEntry from "@/mixins/manualVehicleEntry";
import VehicleSummaryCard from "@/components/Garantie/shared/VehicleSummaryCard";
import { WARRANTY_DETAILS, getWarrantyFeatures } from "@/constants/warrantyDetails";
import { TheMask } from 'vue-the-mask'
import CustomDatepicker from '@/components/CustomInputs/CustomDatepicker';
import GarantieMotoView from "@/components/Garantie/moto/GarantieMotoView";

const INITIAL_FIELDS_VALUES = Object.freeze({
    civilite: 0,
    nom: '',
    prenom: '',
    name_of_signatory: '',
    adresse: '',
    cp: '',
    ville: '',
    tel: '',
    fax: '',
    email: '',
    iban: '',
    garantie: 0,
    immatriculation: '',
    num_serie: '',
    marque_auto: '',
    modele_porteur: '',
    app_commerciale: '',
    jour_mise_en_circulation: 1,
    mois_mise_en_circulation: 1,
    annee_mise_en_circulation: new Date().getFullYear(),
    km_jour_vente: 0,
    cylindree: 0,
    jour_prise_effet: '',
    mois_prise_effet: '',
    annee_prise_effet: '',
    duree: 0,
    jour_de_naissance: '',
    mois_de_naissance: '',
    annee_de_naissance: '',
});


export default {
    name: "GarantieMoto",
    components: { Preloader, TheMask, CustomDatepicker, InsurancePlansCarousel, SelectedPlanSummary, GarantieHeader, GarantieStepper, ManualVehicleEntryModal, VehicleSummaryCard, GarantieMotoView },
    mixins: [garantieFieldsLists, manualVehicleEntry],
    data() {
        return {
            steps: [
                {
                    text: 'INFOS VÉHICULE',
                    icon: `<svg fill="currentColor" width="20" height="20" version="1.1" id="Capa_1" xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink" viewBox="0 0 451.66 451.66" xml:space="preserve"><g id="SVGRepo_bgCarrier" stroke-width="0"></g><g stroke-width="0.368" id="SVGRepo_tracerCarrier" stroke-linecap="round" stroke-linejoin="round"></g><g stroke-width="0.368" id="SVGRepo_iconCarrier"> <g stroke-width="0.368"> <path d="M397.884,217.461c0.881-0.868,1.736-1.715,2.56-2.537c10.797-10.797,15.417-21.94,13.73-33.119 c-1.75-11.611-10.229-21.74-24.521-29.292c-18.274-9.656-74.508-38.746-75.073-39.039c-3.479-1.799-7.688-1.402-10.771,1.017 c-3.081,2.419-4.467,6.415-3.544,10.221l8.234,33.965l-66.88-14.592c-2.841-0.62-5.807,0.026-8.132,1.77l-38.365,28.773 l-42.261-8.965l-36.938-35.517c-2.166-2.082-5.154-3.079-8.135-2.719l-98.995,12c-4.312,0.522-7.794,3.77-8.614,8.036 c-0.822,4.266,1.204,8.574,5.014,10.661l120.526,66.045l-11.528,9.278C103.263,223.254,88.617,217,72.528,217 c-33.719,0-61.151,27.433-61.151,61.151c0,33.72,27.434,61.151,61.151,61.151c32.589,0,59.302-25.626,61.056-57.784h34.394 l6.84,15.801c1.584,3.659,5.188,6.027,9.177,6.027h115.494c2.417,0,4.753-0.875,6.574-2.465c1.003-0.875,10.445-9.12,23.349-20.568 c1.145,32.721,28.104,58.989,61.097,58.989c33.721,0,61.152-27.433,61.152-61.151C451.659,246.931,428.135,221.119,397.884,217.461 z M72.528,299.304c-11.663,0-21.151-9.488-21.151-21.15c0-11.664,9.488-21.152,21.151-21.152s21.151,9.489,21.151,21.152 C93.68,289.815,84.191,299.304,72.528,299.304z M390.506,299.304c-11.663,0-21.15-9.488-21.15-21.15 c0-11.664,9.487-21.152,21.15-21.152s21.152,9.489,21.152,21.152C411.659,289.815,402.169,299.304,390.506,299.304z"></path> </g> </g></svg>`
                },
                {
                    text: 'OFFRE SÉLECTIONNÉE',
                    icon: `<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" class="bi bi-currency-euro" viewBox="0 0 16 16">
                            <path d="M4 9.42h1.063C5.4 12.323 7.317 14 10.34 14c.622 0 1.167-.068 1.659-.185v-1.3c-.484.119-1.045.17-1.659.17-2.1 0-3.455-1.198-3.775-3.264h4.017v-.928H6.497v-.936q-.002-.165.008-.329h4.078v-.927H6.618c.388-1.898 1.719-2.985 3.723-2.985.614 0 1.175.05 1.659.177V2.194A6.6 6.6 0 0 0 10.341 2c-2.928 0-4.82 1.569-5.244 4.3H4v.928h1.01v1.265H4v.928z"></path>
                        </svg>`
                },
                {
                    text: 'PROPRIÉTAIRE VÉHICULE',
                    icon: `<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" class="bi bi-person-fill" viewBox="0 0 16 16">  <path d="M3 14s-1 0-1-1 1-4 6-4 6 3 6 4-1 1-1 1zm5-6a3 3 0 1 0 0-6 3 3 0 0 0 0 6"/></svg>`
                },
                {
                    text: 'SOUSCRIPTION',
                    icon: `<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" class="bi bi-pencil-square" viewBox="0 0 16 16"><path d="M15.502 1.94a.5.5 0 0 1 0 .706L14.459 3.69l-2-2L13.502.646a.5.5 0 0 1 .707 0l1.293 1.293zm-1.75 2.456-2-2L4.939 9.21a.5.5 0 0 0-.121.196l-.805 2.414a.25.25 0 0 0 .316.316l2.414-.805a.5.5 0 0 0 .196-.12l6.813-6.814z"/><path fill-rule="evenodd" d="M1 13.5A1.5 1.5 0 0 0 2.5 15h11a1.5 1.5 0 0 0 1.5-1.5v-6a.5.5 0 0 0-1 0v6a.5.5 0 0 1-.5.5h-11a.5.5 0 0 1-.5-.5v-11a.5.5 0 0 1 .5-.5H9a.5.5 0 0 0 0-1H2.5A1.5 1.5 0 0 0 1 2.5v11z"/></svg>`
                },
                {
                    text: 'DOCUMENTS GARANTIE',
                    icon: `<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" class="bi bi-shield-check" viewBox="0 0 16 16">  <path d="M5.338 1.59a61 61 0 0 0-2.837.856.48.48 0 0 0-.328.39c-.554 4.157.726 7.19 2.253 9.188a10.7 10.7 0 0 0 2.287 2.233c.346.244.652.42.893.533q.18.085.293.118a1 1 0 0 0 .101.025 1 1 0 0 0 .1-.025q.114-.034.294-.118c.24-.113.547-.29.893-.533a10.7 10.7 0 0 0 2.287-2.233c1.527-1.997 2.807-5.031 2.253-9.188a.48.48 0 0 0-.328-.39c-.651-.213-1.75-.56-2.837-.855C9.552 1.29 8.531 1.067 8 1.067c-.53 0-1.552.223-2.662.524zM5.072.56C6.157.265 7.31 0 8 0s1.843.265 2.928.56c1.11.3 2.229.655 2.887.87a1.54 1.54 0 0 1 1.044 1.262c.596 4.477-.787 7.795-2.465 9.99a11.8 11.8 0 0 1-2.517 2.453 7 7 0 0 1-1.048.625c-.28.132-.581.24-.829.24s-.548-.108-.829-.24a7 7 0 0 1-1.048-.625 11.8 11.8 0 0 1-2.517-2.453C1.928 10.487.545 7.169 1.141 2.692A1.54 1.54 0 0 1 2.185 1.43 63 63 0 0 1 5.072.56"/>  <path d="M10.854 5.146a.5.5 0 0 1 0 .708l-3 3a.5.5 0 0 1-.708 0l-1.5-1.5a.5.5 0 1 1 .708-.708L7.5 7.793l2.646-2.647a.5.5 0 0 1 .708 0"/></svg>`
                }
            ],
            currentStep: 1,
            isSending: false,
            isBusy: false,
            garantieMoto: {
                ...INITIAL_FIELDS_VALUES,
                duree: 3
            },
            showManualEntryLink: false,
            showManualEntryPopup: false,
            searchVehicleFilled: false,
            plans: [
                {
                    id: 1,
                    name: 'STANDARD',
                    url: '/generate-pdf-terms-conditions/moto/standard',
                    basePrice: 149
                },
                {
                    id: 2,
                    name: 'COMPLÈTE',
                    url: '/generate-pdf-terms-conditions/moto/complete',
                    basePrice: 199
                }
            ],
            errors: {},
            searchResult: null,
            isClientSearching: false,
            searchVehicleResult: null,
            isVehicleSearching: false,
            effectiveDateInput: this.moment().toDate(),
            circulationDateInput: '',
            selectedPlanPrice: 0,
            birthDateInput: '',
            savedQuote: null,
            initialImmatriculation: '',
        }
    },
    computed: {
        isNewInsurance() {
            return !(this.$route.params.id || (this.savedQuote && this.savedQuote.id));
        },
        existingCustomerFound() {
            return !!this.searchResult;
        },
        existingVehicleFound() {
            return !!this.searchVehicleResult;
        },
        mechanicClientPayment() {
            let client_payment =
                this.$store.state.userInfo && this.$store.state.userInfo.mechanic
                    ? this.$store.state.userInfo.mechanic.client_payment
                    : null;
            return client_payment ? client_payment : "";
        },
        insuranceTypeOptions() {
            return [
                { name: 'STANDARD', value: 1 },
                { name: 'COMPLÈTE', value: 2 }
            ];
        },
        durationOptions() {
            return [
                { name: '3 Mois', value: 1 },
                { name: '6 Mois', value: 2 },
                { name: '12 Mois', value: 3 }
            ];
        },
        availablePlans() {
            const age = this.vehicleAge;

            return this.insuranceTypeOptions.map(opt => {
                const plan = this.plans.find(p => p.id === opt.value);
                if (!plan) return null;

                const detailedFeatures = getWarrantyFeatures({
                    type: plan.id,
                    engine: 0,
                    age: age,
                    category: 'MOTO'
                });

                return {
                    ...plan,
                    detailedFeatures
                };
            }).filter(p => !!p);
        },
        selectedPlanObject() {
            if (!this.garantieMoto.garantie) return null;
            return this.availablePlans.find(plan => plan.id == this.garantieMoto.garantie);
        },
        selectedDurationName() {
            if (!this.garantieMoto.duree) return '';
            const opt = this.durationOptions.find(o => o.value == this.garantieMoto.duree);
            return opt ? opt.name : '';
        },
        selectedPlanDetailedFeatures() {
            const planId = this.garantieMoto.garantie;
            if (!planId) return [];

            const age = this.vehicleAge;

            const features = getWarrantyFeatures({
                type: planId,
                age: age,
                category: 'MOTO'
            });

            return features.filter(f => f.title.toLowerCase() !== 'important' && f.title.toLowerCase() !== 'import');
        },
        vehicleAge() {
            if (!this.circulationDateInput) return 0;
            const dateMec = this.moment(this.circulationDateInput);
            const today = this.moment();
            return today.diff(dateMec, 'years');
        },
        vehicleSummary() {
            let details = [];

            if (this.garantieMoto.immatriculation) {
                details.push({ label: 'Immatriculation', value: this.garantieMoto.immatriculation });
            }

            if (this.garantieMoto.marque_auto) {
                details.push({ label: 'Marque', value: this.garantieMoto.marque_auto });
            }

            if (this.garantieMoto.modele_porteur) {
                details.push({ label: 'Modèle', value: this.garantieMoto.modele_porteur });
            }

            if (this.garantieMoto.cylindree) {
                details.push({ label: 'Cylindrée', value: this.garantieMoto.cylindree + ' cm³' });
            }

            if (this.garantieMoto.km_jour_vente) {
                details.push({ label: 'Kilométrage', value: this.garantieMoto.km_jour_vente + ' km' });
            }

            if (this.circulationDateInput) {
                details.push({
                    label: 'Date de circulation',
                    value: this.moment(this.circulationDateInput).format('DD/MM/YYYY')
                });
            }

            if (this.garantieMoto.num_serie) {
                details.push({ label: 'Numéro de série', value: this.garantieMoto.num_serie });
            }

            return details;
        },
        isVehicleTooOld() {
            if (!this.circulationDateInput) return false;
            const dateMec = this.moment(this.circulationDateInput);
            const today = this.moment();
            const age = today.diff(dateMec, 'years');
            return age > 14;
        }
    },
    watch: {
        'garantieMoto.immatriculation'(val) { if (val) this.clearError('immatriculation'); },
        'garantieMoto.km_jour_vente'(val) { if (val) this.clearError('km_jour_vente'); },
        'garantieMoto.garantie'(val) { if (+val !== 0) this.clearError('garantie'); },
        'garantieMoto.duree'(val) { if (+val !== 0) this.clearError('duree'); },
        'garantieMoto.nom'(val) { if (val) this.clearError('nom'); },
        'garantieMoto.prenom'(val) { if (val) this.clearError('prenom'); },
        'garantieMoto.adresse'(val) { if (val) this.clearError('adresse'); },
        'garantieMoto.cp'(val) { if (val) this.clearError('cp'); },
        'garantieMoto.ville'(val) { if (val) this.clearError('ville'); },
        'garantieMoto.tel'(val) { if (val) this.clearError('tel'); },
        'garantieMoto.email'(val) { if (val) this.clearError('email'); },
        'garantieMoto.name_of_signatory'(val) { if (val) this.clearError('name_of_signatory'); },
        effectiveDateInput(val) { if (val) this.clearError('effective_date'); },
        birthDateInput(val) { if (val) this.clearError('date_of_birth'); },
        '$route'(to, from) {
            if (to.params.id !== from.params.id) {
                if (this.savedQuote && to.params.id == this.savedQuote.id) return;
                // If ID changed (navigating to another existing quote), reload it
                this.loadQuoteInfo();
            }
        }
    },
    methods: {
        getUrl(plan) {
            let apiUrl = this.$store.state.baseUrl;
            return apiUrl + plan.url;
        },
        handleImmatriculationInput(event) {
            this.garantieMoto.immatriculation = this.formatImmatriculation(this.garantieMoto.immatriculation, event);

            // Only trigger search if the value has actually changed from the initial value
            if (this.garantieMoto.immatriculation !== this.initialImmatriculation) {
                this.resetVehicleData();
                this.searchByRegNumber();
            }

            this.clearError('immatriculation');
        },
        resetVehicleData() {
            this.garantieMoto.marque_auto = "";
            this.garantieMoto.modele_porteur = "";
            this.garantieMoto.num_serie = "";
            this.garantieMoto.cylindree = 0;
            this.circulationDateInput = null;
            this.searchVehicleFilled = false;
            this.searchVehicleResult = null;
            this.showManualEntryLink = false;
        },
        searchByRegNumber: DEBOUNCE(function (val) {
            this.executeVehicleSearch();
        }, 500),
        searchByRegNumberImmediately() {
            if (this.garantieMoto.immatriculation && this.garantieMoto.immatriculation.length >= 7) {
                this.searchByRegNumber.cancel();
                this.executeVehicleSearch();
            }
        },
        executeVehicleSearch() {
            this.garantieMoto.immatriculation = this.garantieMoto.immatriculation.toUpperCase();

            let matched = this.garantieMoto.immatriculation.match(/(^([0-9]{2,}-[A-Z]{2,}-[0-9]{2,})+$)|(^([A-Z]{2,}-[0-9]{2,}-[A-Z]{2,})+$)|(^([0-9]{3}-[A-Z]{3}-[0-9]{2})+$)/g);

            if (matched) {
                let url = 'client/registration',
                    data = {
                        regNumber: this.garantieMoto.immatriculation,
                        moto: true
                    };
                this.isVehicleSearching = true;
                this.apiPost(url, data, false)
                    .then(response => {
                        if (response.data.info) {
                            this.searchVehicleResult = response.data.info;
                            this.isManualEntry = false;
                            this.parseVehicleInfo(this.searchVehicleResult);
                            this.searchVehicleFilled = true;
                            this.showManualEntryLink = false;
                        } else {
                            this.showManualEntryLink = true;
                            this.searchVehicleResult = null;
                        }

                        this.isVehicleSearching = false;
                    })
                    .catch(error => {
                        this.isVehicleSearching = false;

                        if (error.response && error.response.status === 422) {
                            let errorMessage = '';
                            if (error.response.data && error.response.data.errors && error.response.data.errors.regNumber && error.response.data.errors.regNumber[0]) {
                                errorMessage = error.response.data.errors.regNumber[0];
                            } else if (error.response.data && error.response.data.message) {
                                errorMessage = error.response.data.message;
                            }

                            if (errorMessage) {
                                if (!this.errors.errors) {
                                    this.$set(this.errors, 'errors', {});
                                }
                                this.$set(this.errors.errors, 'immatriculation', [errorMessage]);
                                this.showManualEntryLink = false;
                                this.searchVehicleResult = null;
                                if (typeof this.searchVehicleFilled !== 'undefined') {
                                    this.searchVehicleFilled = false;
                                }
                                return;
                            }
                        }

                        this.showErrorNotification(error);
                        this.showManualEntryLink = true;
                    });
            } else {
                this.showManualEntryLink = false;
            }
        },
        handleKmInput(event) {
            const val = event.target.value.replace(/\D/g, "");
            this.garantieMoto.km_jour_vente = val ? parseInt(val, 10) : 0;
            this.clearError('km_jour_vente');
        },
        formatImmatriculation(value, event) {
            const isDeleting = event && (event.inputType === 'deleteContentBackward' || event.inputType === 'deleteContentForward');
            const cleanValue = (value || "")
                .toUpperCase()
                .replace(/[^A-Z0-9]/g, "");

            if (!cleanValue) {
                return "";
            }

            const isNumericStart = /^[0-9]/.test(cleanValue);
            const chunkPattern = isNumericStart ? [3, 3, 2] : [2, 3, 2];

            let formatted = "";
            let currentIdx = 0;

            for (let i = 0; i < chunkPattern.length; i++) {
                const chunkSize = chunkPattern[i];
                const chunk = cleanValue.substr(currentIdx, chunkSize);

                if (chunk) {
                    formatted += (formatted ? "-" : "") + chunk;
                }

                currentIdx += chunkSize;
                if (currentIdx >= cleanValue.length) break;
            }

            if (isDeleting && value.endsWith('-')) {
                return value.slice(0, -1);
            }

            return formatted;
        },
        searchClient: DEBOUNCE(function (val) {

            if (this.garantieMoto.tel.length > 2) {
                let url = 'client/searchOne',
                    data = {
                        tel: this.garantieMoto.tel
                    };
                this.isClientSearching = true;
                this.apiGet(url, data, false)
                    .then(response => {
                        if (response.data.client) {
                            this.searchResult = response.data.client;
                            this.parseClientInfo(this.searchResult);
                            this.searchResult = null;
                        }

                        this.isClientSearching = false;
                    })
                    .catch(error => {
                        this.showErrorNotification(error);
                        this.isClientSearching = false;
                    })
            }
        }, 1000),
        fillTheFieldsByImmatriculation() {
            if (this.searchVehicleResult) {
                this.parseVehicleInfo(this.searchVehicleResult);
                this.searchVehicleFilled = true;
                // this.searchVehicleResult = null;
            }
        },
        parseVehicleInfo(info) {
            // /* Car brand */
            if (info.Marque) {
                this.garantieMoto.marque_auto = info.Marque.split('.').join('');
            }
            //
            // /* Modele */
            this.garantieMoto.modele_porteur = info.Modele ? info.Modele.toLocaleString() : null;
            /* Num serie */
            this.garantieMoto.num_serie = info.VIN ? info.VIN : null;

            // /* Cylindree */
            if (info.Cylindree) {
                let cylindree = +info.Cylindree;

                if (cylindree > 650) {
                    this.garantieMoto.cylindree = 3;
                } else if (cylindree >= 125 && cylindree <= 650) {
                    this.garantieMoto.cylindree = 2;
                } else if (cylindree < 125) {
                    this.garantieMoto.cylindree = 1;
                }
            }

            if (info.DateMec) {
                this.circulationDateInput = new Date(info.DateMec);
            }
        },
        fillTheFields() {
            if (this.searchResult) {
                this.parseClientInfo(this.searchResult);
                this.searchResult = null;
            }
        },
        serializeData() {
            return this.garantieMoto;
        },
        onSubmit(event) {
            if (!this.validateStep(3)) return;

            this.garantieMoto.immatriculation = this.garantieMoto.immatriculation.toUpperCase();
            this.validateEffectiveDate();
            this.validateCirculationDate();
            this.validateBirthDate();
            this.isSending = true;
            let url = 'garantie/moto',
                data = this.serializeData();

            if (!this.isNewInsurance) {
                let quoteId = this.$route.params.id || (this.savedQuote ? this.savedQuote.id : null);
                url = 'garantie/moto/' + quoteId + '/edit';
            }

            this.apiPost(url, data)
                .then((response) => {
                    this.isSending = false;
                    this.errors = {};
                    let id = response.data.quote.id;
                    if (response.data.calcQuotes)
                        this.$store.dispatch('updateCalcGaranties', response.data.calcQuotes);
                    this.showSuccessNotification(response.data.message, '');
                    // this.$router.push("/garantie-moto-v2-view/" + response.data.quote.id);
                    this.savedQuote = response.data.quote;
                    this.currentStep++;
                })
                .catch(error => {
                    this.isSending = false;

                    const errorData = error.response.data || {};
                    const status = error.response.status;

                    this.errors = errorData;

                    // Handle 422 error
                    if (status === 422) {
                        this.showErrorNotification(
                            "Les données du formulaire sont invalides. Veuillez vérifier toutes les étapes."
                        );
                        return;
                    }

                    // Default error message
                    if (errorData.message) {
                        this.showErrorNotification(errorData.message);
                    } else {
                        this.showErrorNotification("Une erreur est survenue.");
                    }

                    // Specific exception
                    if (
                        errorData.exception === "App\\Exceptions\\PriceErrorException"
                    ) {
                        this.errors.errors = {
                            error: [errorData.message],
                        };
                    }
                })
        },
        loadQuoteInfo() {
            this.isBusy = true;
            let url = 'garantie/' + this.$route.params.id;
            this.apiGet(url)
                .then(response => {
                    this.savedQuote = response.data.quote;
                    this.garantieMoto = {
                        ...INITIAL_FIELDS_VALUES,
                        ...this.garantieMoto,
                        ...this.parseQuoteInfo(response.data.quote),
                    };
                    this.isBusy = false;
                })
                .catch(error => {
                    this.showErrorNotification(error);
                    this.isBusy = false;
                })
        },
        parseClientInfo(client) {
            this.garantieMoto.civilite = client.civility;
            this.garantieMoto.nom = client.last_name;
            this.garantieMoto.prenom = client.first_name;
            this.garantieMoto.adresse = client.address;
            this.garantieMoto.cp = client.zip;
            this.garantieMoto.ville = client.city;
            this.garantieMoto.tel = client.tel;
            this.garantieMoto.email = client.email;
            this.garantieMoto.iban = client.iban;
            this.garantieMoto.fax = client.fax;
            this.garantieMoto.id_client = client.id;
        },
        handleManualEntrySubmit(data) {
            if (data.immatriculation) {
                let url = "client/registration";
                let reqData = { regNumber: data.immatriculation, moto: true };
                this.isVehicleSearching = true;
                this.apiPost(url, reqData, false)
                    .then(() => {
                        this.isVehicleSearching = false;
                        this.handleManualEntrySubmitGeneric(data, 'garantieMoto');
                    })
                    .catch(error => {
                        this.isVehicleSearching = false;
                        if (error.response && error.response.status === 422) {
                            let errorMessage = '';
                            if (error.response.data && error.response.data.errors && error.response.data.errors.regNumber && error.response.data.errors.regNumber[0]) {
                                errorMessage = error.response.data.errors.regNumber[0];
                            } else if (error.response.data && error.response.data.message) {
                                errorMessage = error.response.data.message;
                            }

                            if (errorMessage) {
                                this.showManualEntryPopup = false;
                                this.garantieMoto.immatriculation = data.immatriculation;
                                if (!this.errors.errors) {
                                    this.$set(this.errors, 'errors', {});
                                }
                                this.$set(this.errors.errors, 'immatriculation', [errorMessage]);
                                this.showManualEntryLink = false;
                                this.searchVehicleResult = null;
                                this.searchVehicleFilled = false;
                                return;
                            }
                        }
                        this.handleManualEntrySubmitGeneric(data, 'garantieMoto');
                    });
            } else {
                this.handleManualEntrySubmitGeneric(data, 'garantieMoto');
            }
        },
        parseQuoteInfo(quote) {
            let parsedQuote = {};

            if (quote.client && quote.client.id) {
                parsedQuote.civilite = quote.client.civility;
                parsedQuote.nom = quote.client.last_name;
                parsedQuote.prenom = quote.client.first_name;
                parsedQuote.name_of_signatory = quote.client.name_of_signatory;
                parsedQuote.adresse = quote.client.address;
                parsedQuote.cp = quote.client.zip;
                parsedQuote.ville = quote.client.city;
                parsedQuote.tel = quote.client.tel;
                parsedQuote.email = quote.client.email;
                parsedQuote.id_client = quote.client.id;
                parsedQuote.fax = quote.client.fax;
                parsedQuote.iban = quote.client.iban;
            }

            if (quote.details) {
                parsedQuote.garantie = quote.details.garantie;
                parsedQuote.immatriculation = quote.details.registration;
                parsedQuote.num_serie = quote.details.serial_number;
                parsedQuote.marque_auto = quote.details.car_brand;
                parsedQuote.modele_porteur = quote.details.modele_porteur;
                parsedQuote.app_commerciale = quote.details.app_commercial;
                parsedQuote.jour_mise_en_circulation = quote.details.day_circulation;
                parsedQuote.mois_mise_en_circulation = quote.details.month_circulation;
                parsedQuote.annee_mise_en_circulation = quote.details.year_circulation;
                parsedQuote.km_jour_vente = quote.details.km_sale_day;
                parsedQuote.cylindree = quote.details.cylinder;

                let effective_day = quote.details.datesParse.day_effective,
                    effective_month = quote.details.datesParse.month_effective - 1,
                    effective_year = quote.details.datesParse.year_effective;


                this.effectiveDateInput = new Date(effective_year, effective_month, effective_day);

                this.circulationDateInput = new Date(
                    quote.details.year_circulation,
                    quote.details.month_circulation - 1,
                    quote.details.day_circulation,
                );

                parsedQuote.duree = quote.details.duration;
            }

            if (quote.client && quote.client.birthday && quote.client.birthday != '1900-01-01 00:00:00') {
                let birth_day = parseInt(quote.client.birthday.split('-')[2]),
                    birth_month = parseInt(quote.client.birthday.split('-')[1]) - 1,
                    birth_year = parseInt(quote.client.birthday.split('-')[0]);

                this.garantieMoto.jour_de_naissance = +birth_day;
                this.garantieMoto.mois_de_naissance = +birth_month;
                this.garantieMoto.annee_de_naissance = +birth_year;

                this.birthDateInput = new Date(birth_year, birth_month, birth_day);
            }

            // Store initial immatriculation to avoid redundant API calls on load
            if (parsedQuote.immatriculation) {
                this.initialImmatriculation = parsedQuote.immatriculation;
            }

            return parsedQuote;
        },
        validateEffectiveDate() {
            if (!this.effectiveDateInput) return false;
            let date = this.moment(this.effectiveDateInput);

            this.garantieMoto.jour_prise_effet = date.format('D');
            this.garantieMoto.mois_prise_effet = date.format('M');
            this.garantieMoto.annee_prise_effet = date.format('YYYY');

            return true;
        },
        resetEffectiveDate() {
            this.garantieMoto.jour_prise_effet = 1;
            this.garantieMoto.mois_prise_effet = 1;
            this.garantieMoto.annee_prise_effet = 1970;
        },
        cleanEffectiveDate() {
            this.garantieMoto.jour_prise_effet = '';
            this.garantieMoto.mois_prise_effet = '';
            this.garantieMoto.annee_prise_effet = '';
        },
        validateCirculationDate() {
            if (!this.circulationDateInput) return false;
            let date = this.moment(this.circulationDateInput);

            this.garantieMoto.jour_mise_en_circulation = date.format('D');
            this.garantieMoto.mois_mise_en_circulation = date.format('M');
            this.garantieMoto.annee_mise_en_circulation = date.format('YYYY');

            return true;
        },
        resetCirculationDate() {
            this.garantieMoto.jour_mise_en_circulation = 1;
            this.garantieMoto.mois_mise_en_circulation = 1;
            this.garantieMoto.annee_mise_en_circulation = 2100;
        },
        validateBirthDate() {
            if (!this.birthDateInput) return false;
            let date = this.moment(this.birthDateInput);

            this.garantieMoto.jour_de_naissance = date.format('D');
            this.garantieMoto.mois_de_naissance = date.format('M');
            this.garantieMoto.annee_de_naissance = date.format('YYYY');

            return true;
        },
        resetBirthDate() {
            this.garantieMoto.jour_de_naissance = 1;
            this.garantieMoto.mois_de_naissance = 1;
            this.garantieMoto.annee_de_naissance = 1970;
        },
        cleanBirthDate() {
            this.garantieMoto.jour_de_naissance = '';
            this.garantieMoto.mois_de_naissance = '';
            this.garantieMoto.annee_de_naissance = '';
        },
        hasError(field) {
            return !!(this.errors.errors && this.errors.errors[field]);
        },
        clearError(field) {
            if (this.errors.errors && this.errors.errors[field]) {
                delete this.errors.errors[field];
                this.errors = { ...this.errors };
            }
        },
        getError(field) {
            if (this.errors.errors && this.errors.errors[field]) {
                return this.errors.errors[field][0];
            }
            return null;
        },
        isValidEmail(email) {
            const re = /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
            return re.test(String(email).toLowerCase());
        },
        validateStep(step) {
            this.errors = { errors: {} };
            let isValid = true;

            if (step === 1) {
                if (!this.isManualEntry && !this.garantieMoto.immatriculation) this.$set(this.errors.errors, 'immatriculation', ['Immatriculation requise.']);
                if (!this.garantieMoto.km_jour_vente) this.$set(this.errors.errors, 'km_jour_vente', ['Kilométrage requis.']);
                if (this.isVehicleTooOld && !this.isManualEntry) this.$set(this.errors.errors, 'eligibility', ["Les critères du véhicule ne permettent pas de souscrire à la garantie choisie."]);
                if (!this.effectiveDateInput) {
                    this.$set(this.errors.errors, 'effective_date', ['Date d\'effet requise.']);
                } else {
                    this.validateEffectiveDate();
                    if (this.circulationDateInput) this.validateCirculationDate();
                }
            } else if (step === 2) {
                if (!this.garantieMoto.garantie || this.garantieMoto.garantie == 0) this.$set(this.errors.errors, 'garantie', ['Garantie requise.']);
                if (!this.garantieMoto.duree || this.garantieMoto.duree == 0) this.$set(this.errors.errors, 'duree', ['Durée requise.']);
            } else if (step === 3) {
                if (!this.garantieMoto.nom) this.$set(this.errors.errors, 'nom', ['Nom requis.']);
                if (!this.garantieMoto.prenom) this.$set(this.errors.errors, 'prenom', ['Prénom requis.']);
                if (!this.garantieMoto.adresse) this.$set(this.errors.errors, 'adresse', ['Adresse requise.']);
                if (!this.garantieMoto.cp) this.$set(this.errors.errors, 'cp', ['Code postal requis.']);
                if (!this.garantieMoto.ville) this.$set(this.errors.errors, 'ville', ['Ville requise.']);
                if (!this.garantieMoto.email) {
                    this.$set(this.errors.errors, 'email', ['Email requis.']);
                } else if (!this.isValidEmail(this.garantieMoto.email)) {
                    this.$set(this.errors.errors, 'email', ["Format d'email invalide."]);
                }
                if (this.garantieMoto.civilite == 3 && !this.garantieMoto.name_of_signatory) {
                    this.$set(this.errors.errors, 'name_of_signatory', ['Nom de la société requis.']);
                }
                this.validateBirthDate();
                this.validateEffectiveDate();
                if (this.circulationDateInput) this.validateCirculationDate();
            }

            if (Object.keys(this.errors.errors).length > 0) {
                isValid = false;
                this.$notify({
                    group: "info",
                    type: "warn",
                    title: "Validation",
                    text: "Veuillez remplir les champs obligatoires."
                });
            }

            return isValid;
        },
        handleStepChange(newStep) {
            if (newStep < this.currentStep) {
                this.currentStep = newStep;
            } else if (newStep > this.currentStep) {
                if (newStep === this.currentStep + 1) {
                    this.goToNextStep();
                    if (this.currentStep == 2) {
                        this.validateEffectiveDate();
                        this.validateCirculationDate();
                    }
                }
            }
        },
        goToNextStep() {
            if (this.validateStep(this.currentStep)) {
                this.currentStep++;
            }
        }
    },
    beforeRouteLeave(to, from, next) {
        this.garantieMoto = INITIAL_FIELDS_VALUES;
        this.currentStep = 1;
        next();
    },
    mounted() {
        if (this.$route.params.id) {
            this.loadQuoteInfo();

            const step = Number(this.$route.query.step);

            if (step) {
                this.currentStep = step;
            }
        }
    }
}
</script>



// WEBPACK FOOTER //
// src/components/Garantie/moto/GarantieMoto.vue